import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, X, Loader2 } from 'lucide-react';
import api from '../lib/api';
import Logo from '../components/Logo';
import Footer from '../components/Footer';

/**
 * VerifyEmailPage — public landing page hit by the link inside the
 * "Verify your GulfBridge email address" transactional email. Calls
 * POST /api/auth/verify-email exactly once and renders the result.
 *
 * Token comes in via ?token=... URL param. Status remains 'pending'
 * while the API request is in flight so users get visual feedback even
 * on slower connections.
 */
export default function VerifyEmailPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') || '';
    const [status, setStatus] = useState('pending'); // 'pending' | 'success' | 'error'
    const [error, setError] = useState('');
    // React.StrictMode (and some email clients' link-prefetchers like Gmail
    // safe-link) can trigger the verify-email POST twice in quick succession.
    // The first call marks the token used; the second would then resolve to
    // "already-used" and flip the UI to an error even though the user is
    // actually verified. This sentinel guarantees the dispatch happens once.
    const didRun = useRef(false);

    useEffect(() => {
        if (didRun.current) return;
        didRun.current = true;
        // `didRun` already guarantees the dispatch runs exactly once across
        // StrictMode's simulated unmount/remount, so no `cancelled` flag is
        // needed here — adding one was actively harmful because StrictMode's
        // cleanup flips it to true BEFORE the network call resolves,
        // permanently stranding the UI in the 'pending' state.
        (async () => {
            if (!token) {
                setStatus('error');
                setError('Missing verification token. Please use the link from your email.');
                return;
            }
            try {
                await api.post('/auth/verify-email', { token });
                setStatus('success');
            } catch (err) {
                const detail = err?.response?.data?.detail || 'Verification failed. The link may be expired or already used.';
                setStatus('error');
                setError(detail);
            }
        })();
    }, [token]);

    return (
        <div className="min-h-screen bg-white flex flex-col" data-testid="verify-email-page">
            <header className="border-b border-zinc-200 px-6 py-4">
                <Logo size="sm" />
            </header>
            <main className="flex-1 flex items-center justify-center px-5 py-12">
                <div className="max-w-md w-full text-center border border-zinc-200 p-8">
                    {status === 'pending' && (
                        <div data-testid="verify-pending">
                            <Loader2 className="w-10 h-10 mx-auto text-zinc-700 animate-spin mb-4" />
                            <h1 className="font-heading font-black text-xl text-zinc-900 mb-2">Verifying your email…</h1>
                            <p className="text-sm text-zinc-500">Hold on while we confirm your verification link.</p>
                        </div>
                    )}
                    {status === 'success' && (
                        <div data-testid="verify-success">
                            <div className="w-12 h-12 mx-auto bg-green-100 border border-green-200 rounded-full flex items-center justify-center mb-4">
                                <Check className="w-6 h-6 text-green-700" />
                            </div>
                            <h1 className="font-heading font-black text-xl text-zinc-900 mb-2">Email verified</h1>
                            <p className="text-sm text-zinc-600 mb-6">Your GulfBridge email has been verified. You can now log in and continue.</p>
                            <button
                                type="button"
                                onClick={() => navigate('/auth')}
                                className="inline-flex items-center justify-center bg-zinc-900 text-white text-sm font-semibold px-6 h-12 hover:bg-zinc-800 transition-colors"
                                data-testid="verify-success-cta"
                            >
                                Continue to Login
                            </button>
                        </div>
                    )}
                    {status === 'error' && (
                        <div data-testid="verify-error">
                            <div className="w-12 h-12 mx-auto bg-red-50 border border-red-200 rounded-full flex items-center justify-center mb-4">
                                <X className="w-6 h-6 text-red-600" />
                            </div>
                            <h1 className="font-heading font-black text-xl text-zinc-900 mb-2">Verification failed</h1>
                            <p className="text-sm text-zinc-600 mb-6">{error}</p>
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center bg-white border border-zinc-900 text-zinc-900 text-sm font-semibold px-6 h-12 hover:bg-zinc-50 transition-colors"
                                data-testid="verify-error-cta"
                            >
                                Contact Support
                            </Link>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
